import { Action } from 'redux';
import {
  IAdviserConnect,
  IUser,
  ICalendlyIFrameEvent,
  IAppState,
} from '../../types/index';
import {
  getAllAdvisers,
  getAllAdviserUserConnects,
  IConnectAdviserBody,
  connectAdviser,
  IFile,
  createAdviserInquiry,
  updateAdviserConnectUser,
  getAdviserConnectByIdSk,
  uploadAdviserConnectAttachments,
  getAllAdviserUserConnectsForUser,
} from 'services';
import { INQUIRY_ERROR } from 'constants/errors';
import { ErrorToast } from 'components/toasts';
import { getPremiumAdviserConnect } from 'helpers/adviser';

export enum Types {
  ADVISER_RESET_FORM = 'ADVISER_RESET_FORM',
  ADVISER_SUBMITTING = 'ADVISER_SUBMITTING',
  GET_ALL_ADVISERS_LOADED = 'GET_ALL_ADVISERS_LOADED',
  GET_ADVISER_CONNECTS = 'GET_ADVISER_CONNECTS',
  GET_ADVISER_CONNECT_LOADED = 'GET_ADVISER_CONNECT_LOADED',
  CREATE_ADVISER_CONNECT = 'CREATE_ADVISER_CONNECT',
  UPDATE_ADVISER_CONNECT = 'UPDATE_ADVISER_CONNECT',
  CALENDY_EVENT_CREATED = 'CALENDY_EVENT_CREATED',
  CALENDY_LOADING = 'CALENDY_LOADING',
  ADVISER_CONNECT_INFO_UPDATED = 'ADVISER_CONNECT_INFO_UPDATED',
  ADVISER_LOADING = 'ADVISER_LOADING',
  ADVISER_FAILED = 'ADVISER_FAILED',
  ADVISER_INQUIRY_LOADED = 'ADVISER_INQUIRY_LOADED',
}

export interface IGetAllAdvisersLoaded extends Action {
  type: Types.GET_ALL_ADVISERS_LOADED;
  data: IUser[];
}

export const GetAllAdvisersLoaded = (data: IUser[]) =>
  ({
    type: Types.GET_ALL_ADVISERS_LOADED,
    data,
  } as IGetAllAdvisersLoaded);

export const GetAllAdvisers = () => async (dispatch) => {
  const result = await getAllAdvisers();
  const data: IUser[] = result.data;
  dispatch(GetAllAdvisersLoaded(data));
};

export interface IGetAdviserConnectsLoaded extends Action {
  type: Types.GET_ADVISER_CONNECTS;
  data: IAdviserConnect[];
}

export interface IGetAdviserConnectLoaded extends Action {
  type: Types.GET_ADVISER_CONNECT_LOADED;
  data: IAdviserConnect;
}

export const GetAdviserConnectsLoaded = (data: IAdviserConnect[]) =>
  ({
    type: Types.GET_ADVISER_CONNECTS,
    data,
  } as IGetAdviserConnectsLoaded);

export const GetAdviserConnectLoaded = (data: IAdviserConnect) =>
  ({
    type: Types.GET_ADVISER_CONNECT_LOADED,
    data,
  } as IGetAdviserConnectLoaded);

export const GetAdviserConnects = () => async (dispatch) => {
  const result = await getAllAdviserUserConnects();
  const data: IAdviserConnect[] = result.data;
  dispatch(GetAdviserConnectsLoaded(data));
};

export const GetAdviserConnectsForCurrentUser = () => async (
  dispatch,
  getState,
) => {
  const { user }: IAppState = getState();
  const result = await getAllAdviserUserConnectsForUser(user.id);
  const data: IAdviserConnect[] = result.data;

  if (data.length > 0) {
    const premiumAdviserConnect = getPremiumAdviserConnect(data);
    if (premiumAdviserConnect) {
      const adRes = await getAdviserConnectByIdSk(
        premiumAdviserConnect.id,
        premiumAdviserConnect.sk,
      );
      dispatch(GetAdviserConnectLoaded(adRes.data));
    }
  }

  dispatch(GetAdviserConnectsLoaded(data));
};

export interface ICreateAdviserConnectLoaded extends Action {
  type: Types.CREATE_ADVISER_CONNECT;
  data: IAdviserConnect;
}

export interface IUpdateAdviserConnectLoaded extends Action {
  type: Types.UPDATE_ADVISER_CONNECT;
  data: IAdviserConnect;
}

export interface IAdviserSubmitting extends Action {
  type: Types.ADVISER_SUBMITTING;
}

export const CreateAdviserConnectLoaded = (data: IAdviserConnect) =>
  ({
    type: Types.CREATE_ADVISER_CONNECT,
    data,
  } as ICreateAdviserConnectLoaded);

export const UpdateAdviserConnectLoaded = (data: IAdviserConnect) =>
  ({
    type: Types.UPDATE_ADVISER_CONNECT,
    data,
  } as IUpdateAdviserConnectLoaded);

export const CreateAdviserConnect = (
  payload: IConnectAdviserBody,
  attachments = [],
) => async (dispatch) => {
  dispatch(AdviserLoading());
  try {
    const result = await connectAdviser(payload as IConnectAdviserBody);
    const data: IAdviserConnect = result.data;

    dispatch(CreateAdviserConnectLoaded(data));
    if (attachments.length > 0) {
      dispatch(
        UploadAdviserUserConnectAttachments(
          result.data.id,
          result.data.sk,
          attachments,
        ),
      );
    }
  } catch {
    dispatch(AdviserFailed());
  }
};

export const UpdateAdviserConnect = (payload: IAdviserConnect) => async (
  dispatch,
) => {
  dispatch(AdviserLoading());
  try {
    const result = await updateAdviserConnectUser(payload);
    dispatch(
      UpdateAdviserConnectLoaded({
        id: payload.id,
        sk: payload.sk,
        ...result.data.Attributes,
      }),
    );
  } catch {
    dispatch(AdviserFailed());
  }
};

export const AdviserSubmitting = () =>
  ({
    type: Types.ADVISER_SUBMITTING,
  } as IAdviserSubmitting);

export interface ICalendlyEventCreated extends Action {
  type: Types.CALENDY_EVENT_CREATED;
  data: {
    eventUrl: string;
  };
}

export const CalendlyEventCreated = (data: { eventUrl: string }) =>
  ({
    type: Types.CALENDY_EVENT_CREATED,
    data,
  } as ICalendlyEventCreated);

export interface IAdviserConnectInfoUpdated extends Action {
  type: Types.ADVISER_CONNECT_INFO_UPDATED;
  data: {
    helpWith: string;
    interestedIn: string[];
    preferredContact: string;
    preferredContactDetails: string;
  };
}

export const AdviserConnectInfoUpdated = (data: {
  helpWith: string;
  interestedIn: string[];
  preferredContact: string;
  preferredContactDetails: string;
}) =>
  ({
    type: Types.ADVISER_CONNECT_INFO_UPDATED,
    data,
  } as IAdviserConnectInfoUpdated);
export interface ICalendarLoading extends Action {
  type: Types.CALENDY_LOADING;
  data: boolean;
}

export const CalendarLoading = (loading: boolean) =>
  ({
    type: Types.CALENDY_LOADING,
    data: loading,
  } as ICalendarLoading);

export interface IAdviserLoading {
  type: Types.ADVISER_LOADING;
}

export interface IAdviserFailed {
  type: Types.ADVISER_FAILED;
}

export interface IAdviserInquiryLoaded {
  type: Types.ADVISER_INQUIRY_LOADED;
}

export const AdviserLoading = () =>
  ({
    type: Types.ADVISER_LOADING,
  } as IAdviserLoading);

export const AdviserFailed = () =>
  ({
    type: Types.ADVISER_FAILED,
  } as IAdviserFailed);

export const AdviserInquiryLoaded = () =>
  ({
    type: Types.ADVISER_INQUIRY_LOADED,
  } as IAdviserInquiryLoaded);

export const CreateAdviserInquiry = (message) => async (dispatch) => {
  dispatch(AdviserLoading());
  try {
    await createAdviserInquiry({ message });
    dispatch(AdviserInquiryLoaded());
  } catch {
    ErrorToast.error({ message: INQUIRY_ERROR });
    dispatch(AdviserFailed());
  }
};

export const CreateCalendlyEvent = (
  calendlyEvent: ICalendlyIFrameEvent,
) => async (dispatch, getState) => {
  const { adviser, user } = getState();
  const adviserConnectInfo = adviser.adviserConnectInfo || {};
  const payload = {
    email: user.email,
    eventUrl: calendlyEvent.payload.event.uri,
    ...adviserConnectInfo,
  };

  dispatch(AdviserSubmitting());
  dispatch(CreateAdviserConnect(payload as IConnectAdviserBody));
  dispatch(
    CalendlyEventCreated({
      eventUrl: calendlyEvent.payload.event.uri,
    }),
  );
};

export const UploadAdviserUserConnectAttachments = (
  id: string,
  sk: string,
  files: IFile[],
) => async (dispatch) => {
  dispatch(AdviserLoading());
  try {
    await uploadAdviserConnectAttachments(files, id);
    // Fetch adviser connect again to get new policies
    const adRes = await getAdviserConnectByIdSk(id, sk);
    dispatch(GetAdviserConnectLoaded(adRes.data));
  } catch {
    dispatch(AdviserFailed());
  }
};

export const GetAdviserConnectByIdSk = (id: string, sk: string) => async (
  dispatch,
) => {
  dispatch(AdviserLoading());
  try {
    const adRes = await getAdviserConnectByIdSk(id, sk);
    dispatch(GetAdviserConnectLoaded(adRes.data));
  } catch {
    dispatch(AdviserFailed());
  }
};
